import { useState, useRef, useEffect, useCallback } from "react";
import { Menu, X, ChevronDown, ChevronRight, Home, Hammer, Droplets, Thermometer, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoColor from "@/assets/logo-color.png";

const serviceSubItems = [
  { label: "Couverture", href: "/services#couverture", icon: Home },
  { label: "Charpente", href: "/services#charpente", icon: Hammer },
  { label: "Zinguerie", href: "/services#zinguerie", icon: Droplets },
  { label: "Isolation", href: "/services#isolation", icon: Thermometer },
];

const navItems = [
  { label: "Accueil", href: "/" },
  { label: "Services", href: "/services", children: serviceSubItems },
  { label: "Réalisations", href: "/realisations" },
  { label: "À propos", href: "/#apropos" },
  { label: "Contact", href: "/contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [emergencyLabel, setEmergencyLabel] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSettings = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("site_settings")
        .select("emergency_mode, emergency_phone, emergency_label")
        .eq("id", "main")
        .single();
      if (data) {
        setEmergencyMode(data.emergency_mode);
        setEmergencyPhone(data.emergency_phone);
        setEmergencyLabel(data.emergency_label);
      }
    };
    fetchSettings();
  }, []);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href || location.pathname.startsWith(href);
  };

  // Close dropdown on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  // Lock background scroll when mobile menu is open
  useEffect(() => {
    if (!mobileOpen) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 200);
  };

  return (
    <header className={`sticky top-0 z-[1000] border-b border-border ${mobileOpen ? "bg-background" : "bg-background/95 backdrop-blur-md"}`}>
      <div className="container-narrow mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img src={logoColor} alt="HNJR Couverture" className="h-12 md:h-14 w-auto max-w-[180px]" width={180} height={79} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.href}
                  className={`${isActive(item.href) ? "nav-link-active" : "nav-link"} inline-flex items-center gap-1`}
                >
                  {item.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                </Link>

                {servicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                    <div className="bg-background rounded-lg border border-border shadow-lg py-2 min-w-[200px]">
                      {item.children.map((sub) => (
                        <a
                          key={sub.label}
                          href={sub.href}
                          className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-primary hover:text-white"
                          style={{ color: "hsl(var(--navy))" }}
                          onClick={(e) => {
                            e.preventDefault();
                            setServicesOpen(false);
                            const [path, hash] = sub.href.split("#");
                            if (window.location.pathname === path) {
                              document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                            } else {
                              navigate(path);
                              setTimeout(() => {
                                document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                              }, 400);
                            }
                          }}
                        >
                          <sub.icon className="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLinkItem key={item.label} item={item} isActive={isActive(item.href)} />
            )
          )}
          <Link to="/devis" className="cta-button text-sm">Devis gratuit</Link>
        </nav>

        <button className="md:hidden text-secondary" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="md:hidden bg-background border-t border-border overflow-y-auto flex flex-col min-h-[calc(100dvh-72px)]">
          <div className="px-6 pt-6 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="border-b border-border">
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className={`w-full flex items-center justify-between py-4 text-lg font-semibold transition-colors ${isActive(item.href) ? "text-primary" : "text-secondary"}`}
                  >
                    {item.label}
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pb-3 space-y-0.5">
                      <div onClick={() => setMobileOpen(false)}>
                        <Link to={item.href} className="flex items-center gap-3 py-3 px-4 text-sm text-muted-foreground hover:text-gold-light hover:bg-muted rounded-lg transition-all font-medium">
                          Tous les services
                        </Link>
                      </div>
                      {item.children.map((sub) => (
                        <div key={sub.label}>
                          <a
                            href={sub.href}
                            className="flex items-center gap-3 py-3 px-4 text-sm text-muted-foreground hover:text-gold-light hover:bg-muted rounded-lg transition-all"
                            onClick={(e) => {
                              e.preventDefault();
                              setMobileOpen(false);
                              const [path, hash] = sub.href.split("#");
                              if (window.location.pathname === path) {
                                document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                              } else {
                                navigate(path);
                                setTimeout(() => {
                                  document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                                }, 400);
                              }
                            }}
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <sub.icon className="w-4 h-4 text-primary" />
                            </div>
                            {sub.label}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div key={item.label} className="border-b border-border" onClick={() => setMobileOpen(false)}>
                  <div className="py-4">
                    <NavLinkItem item={item} isActive={isActive(item.href)} mobile />
                  </div>
                </div>
              )
            )}
          </div>
          <div className="px-6 pb-8 pt-6 space-y-3">
            {emergencyMode ? (
              <>
                <a
                  href={`tel:${emergencyPhone.replace(/\s/g, "")}`}
                  className="cta-button text-base w-full text-center block py-4 rounded-xl inline-flex items-center justify-center gap-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <Phone className="w-5 h-5" />
                  {emergencyLabel || "Urgence"}
                </a>
                <div className="pt-3">
                  <Link
                    to="/devis"
                    className="inline-flex items-center justify-center w-full gap-1 text-center text-secondary font-semibold text-base hover:text-primary transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    Devis gratuit
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <Link to="/devis" className="cta-button text-base w-full text-center block py-4 rounded-xl" onClick={() => setMobileOpen(false)}>
                  Devis gratuit
                </Link>
                <a href="tel:+33189625959" className="flex items-center justify-center gap-2 py-3 text-secondary font-semibold text-base">
                  <Phone className="w-5 h-5 text-primary" />
                  01 89 62 59 59
                </a>
              </>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

const NavLinkItem = ({ item, isActive, mobile }: { item: { label: string; href: string }; isActive: boolean; mobile?: boolean }) => {
  const navigate = useNavigate();

  const handleAnchorClick = useCallback((e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const [path, hash] = href.split("#");
    const targetPath = path || "/";
    
    if (window.location.pathname === targetPath) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(targetPath);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [navigate]);

  const baseClass = mobile
    ? `text-lg font-semibold transition-colors ${isActive ? "text-primary" : "text-secondary"}`
    : (isActive ? "nav-link-active" : "nav-link");

  if (item.href.includes("#")) {
    return (
      <a href={item.href} onClick={(e) => handleAnchorClick(e, item.href)} className={baseClass}>
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.href} className={baseClass}>
      {item.label}
    </Link>
  );
};

export default Header;
